import {Component, EventEmitter, Input, OnDestroy, OnInit, Output} from '@angular/core';
import {FormBuilder, FormControl, FormGroup, Validators} from '@angular/forms';
import {Router} from '@angular/router';
import notify from 'devextreme/ui/notify';
import ValidationEngine from 'devextreme/ui/validation_engine';
import {StatusCodes} from 'http-status-codes';
import {Subject, throwError} from 'rxjs';
import {takeUntil} from 'rxjs/operators';
import {environment} from '../../../../../environments/environment';
import {RegistrationRequest, User} from '../../../models/fusion-auth';
import {LoginViewModel} from '../../../models/general';
import {FusionAuthManagementService, FusionAuthService} from '../../../services/fusion-auth';

@Component({
  selector: 'app-user-profile',
  templateUrl: './user-profile.component.html',
  styleUrls: ['./user-profile.component.scss'],
})
export class UserProfileComponent implements OnInit, OnDestroy {
  @Input() public popupVisible = false;
  @Output() public closeEvent = new EventEmitter();
  public isLoading = false;
  public userForm: FormGroup;
  public user?: User;
  public passwordMode = 'password';
  public passwordButton: any;
  public passwordPattern = /^(?=.*[0-9])(?=.*[!@#$%^\-+&*])[a-zA-Z0-9!@#$%^\-+&*]{8,}$/;
  private onDestroy$: Subject<void> = new Subject<void>();

  constructor(private router: Router, private fb: FormBuilder, public fusionAuth: FusionAuthService, private fusionAuthManagement: FusionAuthManagementService) {
    this.userForm = this.fb.group({
      email: new FormControl(null, Validators.required),
      lastName: new FormControl(null, Validators.required),
      firstName: new FormControl(null, Validators.required),
      oldPassword: new FormControl(null, Validators.required),
      newPassword: new FormControl(null, Validators.required),
    });

    this.passwordButton = {
      icon: 'fa fa-eye',
      type: 'default',
      onClick: () => {
        this.passwordMode = this.passwordMode === 'text' ? 'password' : 'text';
      },
    };
  }

  public ngOnInit() {
    this.user = this.fusionAuth.Profile;
  }

  public onShown() {
    this.userForm.get('email')?.setValue(this.user?.email);
    this.userForm.get('lastName')?.setValue(this.user?.lastName);
    this.userForm.get('firstName')?.setValue(this.user?.firstName);
  }

  public close(): void {
    this.userForm.reset();
    ValidationEngine.resetGroup('userProfileGroup');
    this.closeEvent.emit();
  }

  public submit() {
    const valid = ValidationEngine.validateGroup('userProfileGroup');
    if (valid) {
      this.isLoading = true;
      this.fusionAuth.login({
        loginId: this.user?.email,
        password: this.userForm.get('oldPassword')?.value,
        applicationId: ''
      } as LoginViewModel, true)
        .pipe(takeUntil(this.onDestroy$))
        .subscribe(() => {
          if (!this.user) this.user = {} as User;

          this.user.firstName = this.userForm.get('firstName')?.value;
          this.user.lastName = this.userForm.get('lastName')?.value;

          const user = this.user;

          const request: RegistrationRequest = {
            user,
            registration: {
              applicationId: environment.fusionAuth.applicationId,
              roles: [user.role],
              username: user.username,
              timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            },
          };
          this.fusionAuthManagement.updateUser(user.id, request).pipe(takeUntil(this.onDestroy$)).subscribe(response => {
            if (response.status === StatusCodes.CREATED) {
              this.fusionAuth.changePassword({
                loginId: this.user?.email,
                currentPassword: this.userForm.get('oldPassword')?.value,
                password: this.userForm.get('newPassword')?.value,
              }).pipe(takeUntil(this.onDestroy$)).subscribe(() => {
                this.closeEvent.emit();
                this.userForm.reset();
                this.isLoading = false;
                ValidationEngine.resetGroup('userProfileGroup');
                this.router.navigateByUrl('/login').then();
                return notify('The user information has been update.', 'success');
              }, () => {
                this.isLoading = false;
                return notify('There was a problem updating the user information.', 'error');
              });
            }
          });
        }, (e) => {
          this.isLoading = false;
          notify('Invalid password.', 'error');
          return throwError(e);
        });
    }
  }

  public ngOnDestroy(): void {
    this.onDestroy$.next();
    this.onDestroy$.complete()
  }
}
